import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Ship, Anchor, CalendarCheck, TrendingUp, ArrowDownToLine,
  ArrowUpFromLine, Search, RefreshCw, AlertCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { getDateLocale } from '../i18n';

interface Analytics {
  occupancy_percent: number;
  total_berths: number;
  currently_occupied: number;
  bookings_by_status: Record<string, number>;
  bookings_by_source: Record<string, number>;
  total_revenue: number;
  currency?: string;
}

interface BookingRow {
  id: string;
  booking_code: string;
  guest_name: string;
  vessel_name: string;
  vessel_length_m: number;
  start_datetime: string;
  end_datetime: string;
  status: string;
  electricity: boolean;
  berths?: { label: string } | null;
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function tomorrowISO(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(iso: string): string {
  try {
    const parts = iso.split('T')[0].split('-').map(Number);
    return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString(getDateLocale(), { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  checked_in: 'bg-green-100 text-green-800',
  checked_out: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
  no_show: 'bg-orange-100 text-orange-800',
};

export function DashboardPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { activeMarinaId, isOrgAdmin, profile, loading: authLoading } = useAuth();

  /** Org admin with 2+ marinas must pick one in the header; API calls without marinaId would 500. */
  const needsMarinaSelection =
    !authLoading &&
    isOrgAdmin &&
    (profile?.accessibleMarinaIds?.length ?? 0) > 1 &&
    activeMarinaId === null;
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [todayArrivals, setTodayArrivals] = useState<BookingRow[]>([]);
  const [inHarbor, setInHarbor] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [quickCode, setQuickCode] = useState('');
  const [quickLoading, setQuickLoading] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setInterval>>();

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const fetchData = useCallback(async () => {
    if (authLoading) {
      return;
    }
    if (needsMarinaSelection) {
      setAnalytics(null);
      setTodayArrivals([]);
      setInHarbor([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const today = todayISO();
      const tomorrow = tomorrowISO();
      const [analyticsData, arrivalsData, inHarborData] = await Promise.all([
        api.getAnalytics() as unknown as Promise<Analytics>,
        api.getBookings({ from: today, to: tomorrow, limit: '50' }),
        api.getBookings({ status: 'checked_in', limit: '50' }),
      ]);
      setAnalytics(analyticsData);
      setTodayArrivals((arrivalsData.bookings as unknown as BookingRow[]).filter(
        (b) => b.status === 'confirmed' || b.status === 'pending',
      ));
      setInHarbor(inHarborData.bookings as unknown as BookingRow[]);
    } catch (err) {
      if (err instanceof ApiError) {
        toastRef.current.error(err.message);
      } else if (err instanceof TypeError) {
        toastRef.current.error(t('common.networkError'));
      }
    } finally {
      setLoading(false);
    }
  }, [t, activeMarinaId, needsMarinaSelection, authLoading]);

  useEffect(() => {
    fetchData();
    refreshTimer.current = setInterval(fetchData, 30_000);

    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(refreshTimer.current);
      } else {
        fetchData();
        clearInterval(refreshTimer.current);
        refreshTimer.current = setInterval(fetchData, 30_000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(refreshTimer.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchData]);

  const handleQuickCheckIn = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = quickCode.trim().toUpperCase();
    if (!code) return;
    if (needsMarinaSelection) {
      toast.error(t('dashboard.selectMarinaFirst'));
      return;
    }
    setQuickLoading(true);
    try {
      const bookings = await api.getBookings({ search: code, limit: '1' });
      const booking = (bookings.bookings as unknown as BookingRow[])[0];
      if (!booking) {
        toast.error(t('dashboard.bookingNotFound', { code }));
        setQuickLoading(false);
        return;
      }
      if (booking.status === 'checked_in') {
        await api.updateBookingStatus(booking.id, 'checked_out');
        toast.success(t('dashboard.checkedOut', { code: booking.booking_code }));
      } else if (booking.status === 'confirmed' || booking.status === 'pending') {
        await api.updateBookingStatus(booking.id, 'checked_in');
        toast.success(t('dashboard.checkedIn', { code: booking.booking_code }));
      } else {
        toast.info(t('dashboard.bookingStatus', { code: booking.booking_code, status: booking.status.replace('_', ' ') }));
      }
      setQuickCode('');
      fetchData();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    } finally {
      setQuickLoading(false);
    }
  };

  const handleStatusChange = async (booking: BookingRow, newStatus: string) => {
    if (needsMarinaSelection) {
      toast.error(t('dashboard.selectMarinaFirst'));
      return;
    }
    try {
      await api.updateBookingStatus(booking.id, newStatus);
      toast.success(`${booking.booking_code} → ${newStatus.replace('_', ' ')}`);
      fetchData();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  const stats = [
    {
      label: t('dashboard.occupancy'),
      value: `${analytics?.occupancy_percent ?? 0}%`,
      sub: t('dashboard.berthsOccupied', { current: analytics?.currently_occupied ?? 0, total: analytics?.total_berths ?? 0 }),
      icon: Anchor,
      color: 'text-brand-600 bg-brand-50',
    },
    {
      label: t('dashboard.todaysArrivals'),
      value: String(todayArrivals.length),
      sub: t('dashboard.expectedCheckIns'),
      icon: ArrowDownToLine,
      color: 'text-emerald-600 bg-emerald-50',
    },
    {
      label: t('dashboard.inHarbor'),
      value: String(inHarbor.length),
      sub: t('dashboard.currentlyCheckedIn'),
      icon: Ship,
      color: 'text-blue-600 bg-blue-50',
    },
    {
      label: t('dashboard.totalRevenue'),
      value: analytics
        ? `${(analytics.total_revenue ?? 0).toLocaleString()} ${analytics.currency ?? 'EUR'}`
        : '—',
      sub: t('dashboard.allTimeNonCancelled'),
      icon: TrendingUp,
      color: 'text-amber-600 bg-amber-50',
    },
  ];

  return (
    <div className="space-y-6">
      {needsMarinaSelection && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <p>{t('dashboard.selectMarinaFirst')}</p>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        <button
          onClick={() => { setLoading(true); fetchData(); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-3.5 h-3.5" /> {t('common.refresh')}
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{s.label}</span>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.color}`}>
                <s.icon className="w-4 h-4" />
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900">{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Quick check-in */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <CalendarCheck className="w-4 h-4 text-brand-600" />
          {t('dashboard.quickCheckInOut')}
        </h2>
        <form onSubmit={handleQuickCheckIn} className="flex gap-2">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={quickCode}
              onChange={(e) => setQuickCode(e.target.value)}
              placeholder={t('dashboard.bookingCodePlaceholder')}
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500"
            />
          </div>
          <button
            type="submit"
            disabled={quickLoading || !quickCode.trim()}
            className="px-4 py-2.5 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {quickLoading ? t('dashboard.processing') : t('dashboard.go')}
          </button>
        </form>
        <p className="text-xs text-gray-400 mt-2">{t('dashboard.quickCheckInHint')}</p>
      </div>

      {/* Today's arrivals */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <ArrowDownToLine className="w-4 h-4 text-emerald-600" />
            {t('dashboard.todaysExpectedArrivals')}
          </h2>
          <span className="text-xs text-gray-500">{todayArrivals.length} {t('dashboard.bookings')}</span>
        </div>
        {todayArrivals.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">{t('dashboard.noArrivalsToday')}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {todayArrivals.map((b) => (
              <div key={b.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-semibold text-brand-600">{b.booking_code}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[b.status] ?? 'bg-gray-100'}`}>
                      {b.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="text-sm text-gray-800 mt-0.5 truncate">{b.guest_name} — {b.vessel_name} ({b.vessel_length_m}m)</div>
                  <div className="text-xs text-gray-500">
                    {t('dashboard.berth')} {b.berths?.label ?? '—'} · {formatDate(b.start_datetime)} → {formatDate(b.end_datetime)}
                    {b.electricity && ' · ⚡'}
                  </div>
                </div>
                <button
                  onClick={() => handleStatusChange(b, 'checked_in')}
                  className="shrink-0 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition-colors"
                >
                  {t('dashboard.checkIn')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Currently in harbor */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <ArrowUpFromLine className="w-4 h-4 text-blue-600" />
            {t('dashboard.currentlyInHarbor')}
          </h2>
          <span className="text-xs text-gray-500">{inHarbor.length} {t('dashboard.boats')}</span>
        </div>
        {inHarbor.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">{t('dashboard.noBoatsInHarbor')}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {inHarbor.map((b) => {
              const endParts = b.end_datetime.split('T')[0].split('-').map(Number);
              const endLocal = new Date(endParts[0], endParts[1] - 1, endParts[2], 23, 59, 59);
              const overdue = endLocal < new Date();
              return (
                <div key={b.id} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-brand-600">{b.booking_code}</span>
                      {overdue && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-red-100 text-red-700 flex items-center gap-0.5">
                          <AlertCircle className="w-3 h-3" /> {t('dashboard.overdue')}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-800 mt-0.5 truncate">{b.guest_name} — {b.vessel_name}</div>
                    <div className="text-xs text-gray-500">
                      {t('dashboard.berth')} {b.berths?.label ?? '—'} · {t('dashboard.departure')}: {formatDate(b.end_datetime)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleStatusChange(b, 'checked_out')}
                    className="shrink-0 px-3 py-1.5 bg-gray-600 text-white rounded-lg text-xs font-medium hover:bg-gray-700 transition-colors"
                  >
                    {t('dashboard.checkOut')}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick links */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => navigate('/bookings')} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
          {t('dashboard.allBookings')}
        </button>
        <button onClick={() => navigate('/berths')} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
          {t('dashboard.manageBerths')}
        </button>
      </div>
    </div>
  );
}
